'use client';

import { useMemo, useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import cloudUploadIcon from '@shared/assets/svgs/cloud-upload-outline.svg';
import { Text } from '@design-system/text';
import { cn } from '@design-system/helpers';
import FilePreview from './file-preview';
import { showToast } from '@src/shared/utils/helpers/show-toast';

interface FileUploadProps {
  name: string;
  file?: File | null;
  onChange: (file: File | null) => void;
  allowedFormats?: string;
  maxSize?: string;
  maxFileSizeMB?: number;
  multipleFile?: boolean;
  note?: string;
}

export default function FileUpload({
  name,
  file,
  onChange,
  allowedFormats = '.jpg, .png, .svg, .zip, .pdf',
  maxSize = 'MAX. 800x400px',
  maxFileSizeMB,
  multipleFile = false,
  note,
}: FileUploadProps) {
  const [internalFile, setInternalFile] = useState<File | null>(file ?? null);

  useEffect(() => {
    setInternalFile(file ?? null);
  }, [file]);

  const dropzoneAccept = useMemo(() => {
    const formats = allowedFormats.split(',').map((format) => format.trim());
    const acceptObj: { [key: string]: string[] } = {};

    formats.forEach((format) => {
      if (format === '.jpg') acceptObj['image/jpeg'] = ['.jpg'];
      if (format === '.png') acceptObj['image/png'] = ['.png'];
      if (format === '.svg') acceptObj['image/svg+xml'] = ['.svg'];
      if (format === '.ico') {
        acceptObj['image/x-icon'] = ['.ico'];
        acceptObj['image/vnd.microsoft.icon'] = ['.ico'];
      }
      if (format === '.zip') acceptObj['application/zip'] = ['.zip'];
      if (format === '.pdf') acceptObj['application/pdf'] = ['.pdf'];
      if (format === '.xlsx')
        acceptObj[
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        ] = ['.xlsx'];
      if (format === '.xls') acceptObj['application/vnd.ms-excel'] = ['.xls'];
      if (format === '.xlsm')
        acceptObj['application/vnd.ms-excel.sheet.macroEnabled.12'] = ['.xlsm'];
      if (format === '.csv') acceptObj['text/csv'] = ['.csv'];
      if (format === '.docx')
        acceptObj[
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        ] = ['.docx'];
      if (format === '.doc') acceptObj['application/msword'] = ['.doc'];
      if (format === '.txt') acceptObj['text/plain'] = ['.txt'];
      if (format === '.pptx')
        acceptObj[
          'application/vnd.openxmlformats-officedocument.presentationml.presentation'
        ] = ['.pptx'];
      if (format === '.ppt')
        acceptObj['application/vnd.ms-powerpoint'] = ['.ppt'];
      if (format === '.odt')
        acceptObj['application/vnd.oasis.opendocument.text'] = ['.odt'];
      if (format === '.ods')
        acceptObj['application/vnd.oasis.opendocument.spreadsheet'] = ['.ods'];
      if (format === '.odp')
        acceptObj['application/vnd.oasis.opendocument.presentation'] = ['.odp'];
      if (format === '.rtf') acceptObj['application/rtf'] = ['.rtf'];
      if (format === '.md') acceptObj['text/markdown'] = ['.md'];
      if (format === '.json') acceptObj['application/json'] = ['.json'];
      if (format === '.xml') acceptObj['application/xml'] = ['.xml'];
      if (format === '.yaml' || format === '.yml')
        acceptObj['application/x-yaml'] = ['.yaml', '.yml'];
    });

    return acceptObj;
  }, [allowedFormats]);

  const onDrop = (acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      const uploadedFile = acceptedFiles[0];
      if (
        maxFileSizeMB != null &&
        uploadedFile.size > maxFileSizeMB * 1024 * 1024
      ) {
        showToast({
          title: 'Error',
          description: `File size should not exceed ${maxFileSizeMB} MB.`,
          variant: 'error',
        });
        return;
      }
      setInternalFile(uploadedFile);
      onChange(uploadedFile);
    }
  };

  const handleDelete = () => {
    setInternalFile(null);
    onChange(null);
  };

  const formattedAllowedFormats = useMemo(() => {
    const formats = allowedFormats.split(',').map((format) => format.trim());
    if (formats.length === 1) return formats[0];
    if (formats.length === 2) return `${formats[0]} and ${formats[1]}`;
    return formats.slice(0, -1).join(', ') + ' and ' + formats.slice(-1);
  }, [allowedFormats]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: multipleFile,
    accept: dropzoneAccept,
  });

  return (
    <div className='min-w-0'>
      {internalFile ? (
        <FilePreview
          file={internalFile}
          onDelete={handleDelete}
          onRename={(newName) => {
            if (internalFile) {
              const renamedFile = new File([internalFile], newName, {
                type: internalFile.type,
                lastModified: internalFile.lastModified,
              }) as File & { path?: string; relativePath?: string };

              renamedFile.path = `./${newName}`;
              renamedFile.relativePath = `./${newName}`;

              setInternalFile(renamedFile);
              onChange(renamedFile);
            }
          }}
        />
      ) : (
        <div
          {...getRootProps()}
          className={cn(
            'flex min-h-[144px] cursor-pointer flex-col items-center justify-center gap-[7px] rounded-lg border-2 border-dashed border-primary-200 bg-primary-50 px-4 py-6 text-center transition-all',
            isDragActive && 'border-primary-1000 bg-primary-100'
          )}
        >
          <input
            {...getInputProps()}
            name={name}
            className='hidden'
          />
          <img
            alt=''
            src={cloudUploadIcon}
            width={24}
            height={24}
            className='size-10! shrink-0'
            aria-hidden='true'
          />
          <Text className='text-sm leading-normal text-gray-800'>
            <Text
              variant='span'
              className='font-semibold'
            >
              Click to upload
            </Text>
            <Text
              variant='span'
              className='font-normal'
            >
              {' '}
              or drag and drop
            </Text>
          </Text>
          <Text className='text-xs leading-normal text-gray-800 opacity-80'>
            Supports {formattedAllowedFormats} files
            {maxSize ? ` (MAX. ${maxSize})` : ''}
          </Text>
        </div>
      )}
      {note && <Text className='text-xs text-gray-600 mt-2'>{note}</Text>}
    </div>
  );
}
